import { faArrowDown, faArrowUp, faCog, faExpand, faMinus, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'assets/css/stx-chart.css';
import 'assets/scss/ChartIQ/chartiq.scss';
import { colors, styled, ThemeConsumer, ThemeName, themes } from 'assets/theme';
import { NYSEHoursOfOperations } from 'data/nyse';
import { IStudyGroup } from 'models/study-group';
import { useCallback, useEffect, useRef, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { connect } from 'react-redux';
import { keycloakSelector } from 'redux/keycloak/selectors';
import StudyGroupService from 'services/study-group.service';
import { subscribeListener, unsubscribeListener } from '../../redux/ably/sagas';
import config from '../../redux/chart-iq/chart/config';
import { ChartUIWrapper, Legend } from './chart-components';
import { DrawIQ } from './Draw';
import { Quotefeed } from './Quotefeed';

const CIQ = (window as any).CIQ
CIQ.Market.NYSE = NYSEHoursOfOperations;

CIQ.ChartEngine.prototype.registerHTMLElements=function(){
  const c=this.chart.container;
  for(const control in CIQ.ChartEngine.htmlControls){
    if(typeof this.chart[control]==="undefined" && typeof this.controls[control]==="undefined"){
      if(!this.allowZoom && control==="chartControls") { continue; }
      let el=c.querySelector("." + control);
      if(el){
        this.chart[control]=el;
        this.controls[control]=el;
      }else{
        const rawHTML=CIQ.ChartEngine.htmlControls[control];
        if(!rawHTML) { continue; }
        const div=document.createElement("DIV");
        div.innerHTML=rawHTML;
        el=div.firstChild;
        c.appendChild(el);
        this.chart[control]=el;
        this.controls[control]=el;
        CIQ.appendClassName(el,control);
      }
    }
  }
  const chartControls = this.controls.chartControls
  const home = this.controls.home;

  if(chartControls){
    const zoomIn=chartControls.querySelector(".stx-zoom-in");
    const zoomOut=chartControls.querySelector(".stx-zoom-out");

    CIQ.safeClickTouch(zoomIn,((self) =>(e: any) => { if(self.allowZoom) { self.zoomIn(e); } e.stopPropagation(); })(this));
    CIQ.safeClickTouch(zoomOut,((self) =>(e: any) => { if(self.allowZoom) { self.zoomOut(e); } e.stopPropagation(); })(this));
    if(!CIQ.touchDevice){
      this.makeModal(zoomIn);
      this.makeModal(zoomOut);
    }
  }
  if(home){
    CIQ.safeClickTouch(home, ((self) => {
      return (e: any) => {
        e.stopPropagation();
        // If we are not in historical mode then scroll home
        if (!self.isHistoricalMode()){
          self.home({ animate: true });
          return;
        }
        // If in historical mode delete any range the chart might have to prevent setting it again and call loadChart
        // This will be fast than scrolling and paginating forward as the chart progresses towards the current day
        delete self.layout.range;
        self.changing = false
        self.isHistoricalModeSet=false; // turn off historical mode before anything else is done
        self.loadChart(self.chart.symbol); // no need to call home in loadChart
      };
    })(this));
    if(!CIQ.touchDevice){
      this.makeModal(home);
    }
  }
};

export const ChartResponsiveSize = Object.freeze({
  LARGE: 'break-lg',
  MEDIUM: 'break-md',
  SMALL: 'break-sm',
});

export const calculateResponsiveSize = () => {
  if (window.innerWidth > 800) {
    return ChartResponsiveSize.LARGE;
  } else if (window.innerWidth > 584) {
    return ChartResponsiveSize.MEDIUM;
  }
  return ChartResponsiveSize.SMALL;
};

interface IChartIQParams {
  connectionStatus: number
  id: number
}

interface IStateProps {
  keycloak: any
}

type Props = IChartIQParams & IStateProps

const ChartIQ: React.FunctionComponent<Props> = ({ connectionStatus, id, keycloak}) => {
  const [symbol, setSymbol] = useState('');
  const [stxx, setStxx] = useState<any>(null);
  const [periodicity, setPeriodicity] = useState({ timeUnit: '', interval: 0 });
  const [chartType, setChartType] = useState('');
  const [showCrosshairs, setShowCrosshairs] = useState(true);
  const [showDrawingToolbar, setShowDrawingToolbar] = useState(false);
  const [chartTop, setChartTop] = useState(0);
  const [isLoadingPeriodicity, setIsLoadingPeriodicity] = useState(false);
  const [enableDateFilter, setEnableDateFilter] = useState(false);
  const [connectionState, setConnectionState] = useState<number>(connectionStatus);
  const [minDate, setMinDate] = useState<Date>()
  const [totalTicks, setTotalTicks] = useState(0)
  const chartIQRef = useRef<HTMLDivElement>(null);
  const currentTheme = useRef(ThemeName.Dark);
  const chartIQContextRef = useRef();

  const defaultStudies = ['obosIndividual', 'obosInline', 'vix', 'rVol', 'signalAnalysis', 'volume'];

  const getLayoutFromStorage =(id: number) => {
    const layout = CIQ.localStorage.getItem(`chart-layout-${id}`);
    return layout != null ? JSON.parse(layout) : layout;
  }

  const checkAccessToStudies = (id: number, keycloak: any) => {
    const layout = getLayoutFromStorage(id);

    if (layout !== null) {
      const hasAccessToStudy = (studyName: string) => {
        const group: IStudyGroup = StudyGroupService.getStudyGroup(studyName);
        return !group || keycloak.hasRole([group.name]);
      }

      const removeUnicodeChars = (str: string) => str.replace(/[^\\x00-\\x7F]/g, "");

      const activeStudiesKeys = layout.studies ? Object.keys(layout.studies) : [];
      for (let key of activeStudiesKeys) {
        const cleansed = removeUnicodeChars(key);
        if (hasAccessToStudy(cleansed))
            continue;

        delete layout.studies[key];
        delete layout.panels[key];
      }

      CIQ.localStorage.setItem(`chart-layout-${id}`, JSON.stringify(layout));
    }
  }

  const updateComparisonSeries = () => null;

  const changeDrawings = useCallback(() => {
    const tmp = stxx.exportDrawings();
    if (tmp.length > 0) {
      CIQ.localStorage.removeItem(stxx.symbol);
    } else {
      CIQ.localStorageSetItem(stxx.symbol, JSON.stringify(tmp));
    }
  }, [stxx]);

  const layoutChangedAndSave = useCallback(() => {
    setChartTop(stxx.chart.top);
  }, [stxx]);

  const toggleCrosshairsAndSave = () => {
    setShowCrosshairs(v => !v);
  };

  useEffect(() => {
    if (stxx) {
      stxx.layout.crosshair = showCrosshairs;
    }
  }, [stxx, showCrosshairs])

  const toggleDrawingToolbar = () => {
    setShowDrawingToolbar(!showDrawingToolbar);
  };

  const setChartTypeAndSave = (ciq: any, newChartType: any) => {
    setChartType(newChartType);
    if (stxx) {
      if (newChartType.aggregationEdit && stxx.layout.aggregationType !== newChartType.type) {
        stxx.setChartType('none');
        stxx.setAggregationType(newChartType.type);
      } else {
        stxx.setAggregationType(newChartType.type);
        stxx.setChartType(newChartType.type);
      }
      stxx.draw();
    }
  };

  const setPeriodicityWithLoader = (ciq: any, newPeriodicity: any) => {
    setPeriodicity(newPeriodicity);
    if (stxx) {
      stxx.setPeriodicity(newPeriodicity);
    }
  };

  useEffect(() => {
    if (stxx && symbol !== '') {
      delete stxx.layout.range
      stxx.newChart(symbol, null, null);
      stxx.setMarket(CIQ.Market.NYSE)
    }
  }, [stxx, symbol])

  const saveLayout = useCallback(() => {
    if (stxx) {
      const savedLayout = JSON.stringify(stxx.exportLayout({ withSymbols: true }));
      CIQ.localStorageSetItem(`chart-layout-${id}`, savedLayout);
      CIQ.localStorageSetItem(`chart-preferences-${id}`, JSON.stringify(stxx.exportPreferences()));
    }
  }, [stxx, id]);

  useEffect(() => {
    if (stxx) {
      stxx.setStyle('cq-attrib-container', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_xaxis', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_xaxis', 'borderRightColor', 'red')
      stxx.setStyle('stx_grid_border', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_panel_border', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_xaxis_dark', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_yaxis', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_yaxis_dark', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_bar_even', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_candle_shadow', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_candle_shadow_even', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_candle_shadow_up', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_candle_shadow_down', 'color', themes[currentTheme.current].core.textColor)
      stxx.setStyle('stx_candle_up', 'borderLeftColor', colors.accents.good.base)
      stxx.setStyle('stx_candle_down', 'borderLeftColor', colors.accents.bad.base)
      stxx.setStyle('stx_grid', 'color', '#54606D')
      stxx.setStyle('stx_grid', 'opacity', 0.2)
      stxx.setStyle('stx_grid_dark', 'color', '#54606D')
      stxx.setStyle('stx_grid_dark', 'opacity', 0.2)

      stxx.draw()
    }
  }, [currentTheme, stxx])

  useEffect(() => {
    if (connectionState === WebSocket.OPEN) {
      if (stxx) {
        const symbolConfig = config.symbols.find(sym => sym.id === symbol);
        if (symbolConfig) {
          stxx.newChart(symbol, null, null);
        }
      }
    }
  }, [connectionState, stxx, symbol]);

  useEffect(() => {
    subscribeListener('store:connected', 1, ({ data }: any) => {
      setConnectionState(data);
    });

    checkAccessToStudies(id, keycloak);

    return () => unsubscribeListener('store:connected', 1);
  // eslint-disable-next-line
  }, []); 

  useEffect(
    () => {
      if (stxx) {
        // setup callbacks
        stxx.callbacks.symbolChange = updateComparisonSeries;
        stxx.callbacks.drawing = changeDrawings;
        stxx.callbacks.layout = layoutChangedAndSave;

        // load settings, if present
        const layout = getLayoutFromStorage(id);

        if (layout !== null) {

          if (layout.symbols.find((sym: any) => sym.symbol.includes('ES') && sym.symbol.includes('C')) === undefined) {
            stxx.importLayout(layout, {
              cb: () => {
                if (!stxx.chart.symbol) {
                  return;
                }

                const memory = CIQ.localStorage.getItem(stxx.chart.symbol.toUpperCase());
                if (memory !== null) {
                  const parsed = JSON.parse(memory);
                  if (parsed) {
                    stxx.importDrawings(parsed);
                    stxx.draw();
                  }
                }
              },
              managePeriodicity: true,
            });

            if (layout.symbols.length > 0) {
              setSymbol(layout.symbols[0].symbol);
            }
          }
        } else {
          // let's set the default studies
          defaultStudies.forEach((studyName: string) => CIQ.Studies.addStudy(stxx, studyName))
        }
        stxx.setMaxTicks(200);
      }
    },
    // eslint-disable-next-line
    [stxx, changeDrawings, layoutChangedAndSave, id]
  );

  useEffect(() => {

    const ciq = initChart();

    setStxx(ciq);

    initQuoteFeed();
    initChartProps();

    function initChartProps() {
      ciq.chart.xAxis.displayGridLines = true;
      ciq.preferences.currentPriceLine = true;
      (ciq.chart as any).yAxis.width = 75;
      setChartTop((ciq.chart as any).top);
    }

    function initQuoteFeed() {
      const token = keycloak.getToken();
      if (token) {
        const qf = Quotefeed(id, token, ciq, setIsLoadingPeriodicity, setMinDate, setTotalTicks);
        const qfConfig = { refreshInterval: 0, forwardPaginationRetryInterval: 1 };
        ciq.attachQuoteFeed(qf, qfConfig);
      }
    }

    function initChart() {
      const container = chartIQRef.current;
      const ciq = new CIQ.ChartEngine({ container });
      ciq.streamParameters.maxWait = 250;
      ciq.maxMasterDataSize = 20000;
      return ciq;
    }
  // eslint-disable-next-line
  }, [id]);

  useEffect(
    () => {
      saveLayout();
    },
    [showCrosshairs, symbol, periodicity, chartTop, saveLayout]
  );

  // this function is used for debugging 
  let tickStatusObject = {"previous": 0, "current": 0, "state": "green"} as any;
  function checkTickStatus (totalTicks: number) {
    tickStatusObject.currentTicks = totalTicks;
    //console.log("Total ticks : " + totalTicks);
    if(totalTicks === 0){
      tickStatusObject.previous = totalTicks;
      tickStatusObject.state = "red";
      return ;
    }
    if(tickStatusObject.previous === 0){
      // current is > 0
      tickStatusObject.previous = totalTicks;
      tickStatusObject.state = "green";
      return ;
    }
   
  }
  
  return (
    <ThemeConsumer>
      {({ themeName }) => {
        if (!themeName) {
          return
        }
        currentTheme.current = themeName as ThemeName;
        // _setCurrentTheme(themeName)
        
        return (
          <ThemedChartWrapper currentTheme={currentTheme.current}>
            <cq-context ref={chartIQContextRef} id={`chartiq-${id}`}>
              <cq-ui-manager />
              <ChartUIWrapper
                id={id}
                ciq={stxx}
                symbol={symbol}
                setSymbolAndSave={setSymbol}
                periodicity={periodicity}
                setPeriodicityWithLoader={setPeriodicityWithLoader}
                setChartTypeAndSave={setChartTypeAndSave}
                chartType={(stxx && stxx.layout.chartType) || chartType}
                toggleCrosshairsAndSave={toggleCrosshairsAndSave}
                showCrosshairs={showCrosshairs}
                toggleDrawingToolbar={toggleDrawingToolbar}
                saveLayout={saveLayout}
                enableDateFilter={enableDateFilter}
                setEnableDateFilter={setEnableDateFilter}
                minDate={minDate}
                showDrawingToolbar={showDrawingToolbar}
                />
              <DrawIQ showDrawingToolbar={showDrawingToolbar} ciq={stxx}  />
              <Legend ciq={stxx} symbol={symbol} chartTop={chartTop} />
              <ChartArea>
                <div ref={chartIQRef} id={`chartiq-container-${id}`} className='chartContainer chartContainerMain'>
                  {
                    isLoadingPeriodicity ?
                    <div style={{ position: 'absolute', display: 'block', height: '32px', width: '32px', left: '50%', top: '50%' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="32" height="32" fill="white">
                        <path opacity=".25" d="M16 0 A16 16 0 0 0 16 32 A16 16 0 0 0 16 0 M16 4 A12 12 0 0 1 16 28 A12 12 0 0 1 16 4"/>
                        <path d="M16 0 A16 16 0 0 1 32 16 L28 16 A12 12 0 0 0 16 4z">
                          <animateTransform attributeName="transform" type="rotate" from="0 16 16" to="360 16 16" dur="0.8s" repeatCount="indefinite" />
                        </path>
                      </svg>
                    </div>
                    :
                    <div />
                  }
                  <div className="chartControls">
                    <div className="stx_chart_controls" style={{bottom: '22px'}}>
                      <div className="chartSize">
                        <span className="stx-zoom-out">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faMinus} />
                        </span>
                        <span className="stx-zoom-in">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faPlus} />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="iconsTemplate">
                    <div className="stx-panel-control">
                      <div className="stx-panel-title" />
                      <div className="stx-btn-panel">
                        <span className="stx-ico-up">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faArrowUp} />
                        </span>
                      </div>
                      <div className="stx-btn-panel">
                        <span className="stx-ico-focus">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faExpand} />
                        </span>
                      </div>
                      <div className="stx-btn-panel">
                        <span className="stx-ico-down">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faArrowDown} />
                        </span>
                      </div>
                      <div className="stx-btn-panel">
                        <span className="stx-ico-edit">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faCog} />
                        </span>
                      </div>
                      <div className="stx-btn-panel">
                        <span className="stx-ico-close">
                          <FontAwesomeIcon style={{ cursor: 'pointer' }} size="1x" icon={faTimes} />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </ChartArea>
              <FooterArea>
                <div style={{ height: '15px' }}>
                  <Container>
                    {checkTickStatus(totalTicks)}
                      <OuterBar statusBarcolor={totalTicks > 0 ? "#00cd82" : "#ff0000"}>
                      <InnerBar statusBarcolor={totalTicks > 0 ? "#00cd82" : "#ff0000"} barWidth={Math.min((totalTicks / 20000) * 100, 100)} />
                    </OuterBar>
                  </Container>
                </div>
              </FooterArea>
            </cq-context>
          </ThemedChartWrapper>
        )
      }}
    </ThemeConsumer>
  );
};

const ThemedChartWrapper = styled.div<{currentTheme: string}>`
	min-width: 350px;
	height: 100%;
  font-size: 0.8em;
  .stx-chart-panel {
      display: none;
  }
  > div, > span {
    border:0;
    font-family: $base-font;
    font-weight: 400;
    font-size:$font-size-base;
    text-align:left;
    line-height: 1.6em;
  }

  cq-context {
    display: flex;
    flex-flow: column;
    padding-bottom: 10px;
    height: 100%;
    width: 100%;
  	touch-action:none;
    background-color: ${({ theme }) => theme.core.alternateChartBackground};
  }

  .chartContainer {
    background-color: ${({ theme }) => theme.core.alternateChartBackground};
  }

  .stx-panel-title {
    color: ${({ theme }) => theme.core.textColor};
  }

  .chart-title {
    display: none;
  }

  .stx-zoom-in {

  }

  .stx_jump_today {
    background-color: ${({ theme }) => theme.core.alternateBackground};
    color: ${({ theme }) => theme.core.textColor};
    span {
      background-position: -448px ${({ currentTheme }) => currentTheme === 'dark' ? '-50px' : '-25px' } ;
    }
  }
  .chartSize {
    background-color: ${({ theme }) => theme.core.alternateBackground};
    color: ${({ theme }) => theme.core.textColor};
    display: grid;
    grid-auto-flow: column;
    span {
      background-image: none;
      display: grid;
      justify-content: center;
      align-content: center;
    }
  }
  .stx-panel-control {
    display: block;
  }
  .stx-btn-panel {
    color: ${({ theme }) => theme.core.textColor};
    display: inline-block !important;
    span {
      background-image: none;
      display: grid;
      justify-content: center;
      align-content: center;
    }
  }

  .mSticky {
    font-weight: bold;
    border-radius: 0;
    -webkit-border-radius: 0;
    color: ${({ theme }) => theme.core.textColor};
    background-color: ${({ theme }) => theme.core.alternateBackground};
    border: solid 2px ${({ theme }) => theme.core.lightBackground};
    padding: 5px;
  }

  .mStickyInterior {
    padding: 0;
  }
  .stx_chart_controls { /* Chart Control module */
    position: absolute;
    display: block;
    bottom: 60px;
    left: initial;
    margin-left:15px;
    height: 27px;
    margin-bottom: 15px;
    width: auto;
    text-align: center;
    z-index: 30;
  }
`

const ChartArea = styled.div`
	flex-grow: 1;
  background-color: ${({ theme }) => theme.core.alternateChartBackground};
  color: ${({ theme }) => theme.core.textColor};
	z-index: 1;
	margin-left:20px;
	margin-right: 0;
	border-radius: 0px 0px 3px 3px;
	.break-sm & { /* Remove padding around chart when the app is set to breakpoint-sm*/
		padding:0;
	}
`

const FooterArea = styled.div`
  height: 20px;
  bottom: 0px;
  width: 100%;
  padding: 0 15px;
  margin:2px;
  overflow: hidden;
`
const Container = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr 100px;
  justify-items: center;
  height: 100%;
  align-items: center;
`;

const OuterBar = styled.div<{statusBarcolor: string}>`
width: 100%;
border: solid 1px ${colors.accents.good.base};
height: 100%;
border-color:  ${({ statusBarcolor }) => statusBarcolor};
`;

const InnerBar = styled.div<{barWidth: number, statusBarcolor: string}>`
  width: ${({ barWidth }) => barWidth}%;
  background: ${({ statusBarcolor }) => statusBarcolor};
  height: 100%;
`;

const mapStateToProps = (state: any) => ({
  keycloak: keycloakSelector(state)
});


export default connect(mapStateToProps)(ChartIQ)


